import format from 'date-fns/format'
import FileSaver from 'file-saver'
import { db, FirebaseDocRefType } from 'firebase/index'
import {
  dateToString,
  parseDateWithHyphen,
  parseFrozenDeliveryTypeForYamato,
  parseHankakuToZenkaku,
  parsePhoneNumber,
  parseTimeToNumber,
} from 'lib/helper'
import { DeliveryOrderCsvRow, OrderType, SortKey, SortOrder } from 'types/checkout'
import { PickupableTimeType } from 'types/bread'
import { BasketType } from 'types/basket'
import { StoreShippingType } from 'types/storeShipping'
import { StoreDeliveryBreadType } from '../types/storeDeliveryBread'

// 注文の売上を合計する
export const sumOrderAmount = (orders: OrderType[]): number => {
  return orders.reduce((prev, order) => {
    return prev + order.amount
  }, 0)
}

// 注文の個数を合計する
export const sumOrderQuantity = (orders: OrderType[]): number => {
  return orders.reduce((prev, order) => {
    return (
      prev +
      order.items.reduce((prev, item) => {
        return prev + item.quantity
      }, 0)
    )
  }, 0)
}

// 顧客のこれまでの受取回数を取得する
export const sumOrderTimes = async (
  currentDate: Date,
  storeRef: FirebaseDocRefType,
  userId: string
): Promise<number> => {
  const promises = []
  promises.push(
    storeRef
      .collection('store_baskets')
      .where('user_id', '==', userId)
      .orderBy('date', 'desc')
      .startAt(dateToString(currentDate))
      .get()
      .then((snapshots) => snapshots.docs.length)
  )
  promises.push(
    storeRef
      .collection('store_shippings')
      .where('user_id', '==', userId)
      .orderBy('scheduled_delivery_date', 'desc')
      .startAt(dateToString(currentDate))
      .get()
      .then((snapshots) => snapshots.docs.length)
  )
  promises.push(
    db
      .collection('orders')
      .where('shop_id', '==', storeRef.id)
      .where('user_id', '==', userId)
      .orderBy('created_at', 'desc')
      .startAt(currentDate)
      .get()
      .then((snapshots) => snapshots.docs.length)
  )
  const times: number[] = await Promise.all(promises)
  return times.reduce((prev, time) => prev + time, 0)
}

// レジ袋の枚数を合計する
export const sumShoppingBagQuantity = (orders: OrderType[]): number => {
  return orders.reduce((prev, order) => {
    if (order.shopping_bags) {
      return (
        prev +
        order.shopping_bags.reduce((prev, shoppingBag) => {
          return prev + shoppingBag.quantity
        }, 0)
      )
    } else {
      return prev
    }
  }, 0)
}

export const togglePrepared = (checkoutId: string, nextState: boolean, storeRef: FirebaseDocRefType): Promise<void> => {
  return storeRef
    .collection('store_checkouts')
    .doc(checkoutId)
    .update({ is_prepared: nextState })
    .catch((error) => {
      console.error(error)
    })
}

export const toggleDelivered = (
  checkoutId: string,
  nextState: boolean,
  storeRef: FirebaseDocRefType
): Promise<void> => {
  return storeRef
    .collection('store_checkouts')
    .doc(checkoutId)
    .update({ is_delivered: nextState })
    .catch((error) => {
      console.error(error)
    })
}

export const getEarliestCheckoutOrder = (orders: OrderType[], userId: string): number => {
  return orders
    .filter((order) => order.user_id === userId)
    .reduce(
      (prev, order) =>
        Math.max(prev, order.checkout_at ? order.checkout_at.toDate().getTime() : order.updated_at.toDate().getTime()),
      0
    )
}

export const getEarliestPickupOrder = (orders: OrderType[], userId: string): number => {
  return orders
    .filter((order) => order.user_id === userId)
    .reduce((prev, order) => Math.max(prev, parseTimeToNumber(order.pickup_time)), 0)
}

export const sumOrderByBreadQuantity = (pickupableTimes: PickupableTimeType[]): number => {
  return pickupableTimes.reduce((prev, pickupableTime) => {
    return (
      prev +
      pickupableTime.choices.reduce((_prev, choice) => {
        return _prev + choice.quantity
      }, 0)
    )
  }, 0)
}

export const sortOrdersByKey = (
  sortKey: SortKey,
  sortOrder: SortOrder,
  a: BasketType,
  b: BasketType,
  orders: OrderType[]
) => {
  // keyと昇順/降順に応じて受取番号の並び替えを行う（デフォルトは注文日時の降順）
  if (sortKey === 'checkout_at' && sortOrder === 'asc') {
    return getEarliestCheckoutOrder(orders, a.user_id) < getEarliestCheckoutOrder(orders, b.user_id) ? -1 : 1
  } else if (sortKey === 'checkout_at' && sortOrder === 'desc') {
    return getEarliestCheckoutOrder(orders, a.user_id) > getEarliestCheckoutOrder(orders, b.user_id) ? -1 : 1
  } else if (sortKey === 'pickup_time' && sortOrder === 'asc') {
    return getEarliestPickupOrder(orders, a.user_id) < getEarliestPickupOrder(orders, b.user_id) ? -1 : 1
  } else if (sortKey === 'pickup_time' && sortOrder === 'desc') {
    return getEarliestPickupOrder(orders, a.user_id) > getEarliestPickupOrder(orders, b.user_id) ? -1 : 1
  } else {
    return getEarliestCheckoutOrder(orders, a.user_id) > getEarliestCheckoutOrder(orders, b.user_id) ? -1 : 1
  }
}

export const exportDeliveryOrders = ({
  date,
  scheduledDeliveryDate,
  storeName,
  phoneNumber,
  firstPostalCode,
  secondPostalCode,
  state,
  city,
  street,
  building,
  customerCode,
  feeManagementNumber,
  orders,
  storeShippings,
  storeDeliveryBreads,
}: {
  date: string
  scheduledDeliveryDate: string
  storeName: string
  phoneNumber: string
  firstPostalCode: string
  secondPostalCode: string
  state: string
  city: string
  street: string
  building: string
  customerCode: string
  feeManagementNumber: string
  orders: OrderType[]
  storeShippings: StoreShippingType[]
  storeDeliveryBreads: StoreDeliveryBreadType[]
}) => {
  const filename = `sacri_ヤマト送り状_${date}`
  const header =
    'お客様管理番号,送り状種類,クール区分,伝票番号,出荷予定日,お届け予定日,配達時間帯,お届け先コード,お届け先電話番号,お届け先電話番号枝番,お届け先郵便番号,お届け先住所,お届け先アパートマンション名,お届け先会社・部門１,お届け先会社・部門２,お届け先名,お届け先名(ｶﾅ),敬称,ご依頼主コード,ご依頼主電話番号,ご依頼主電話番号枝番,ご依頼主郵便番号,ご依頼主住所,ご依頼主アパートマンション,ご依頼主名,ご依頼主名(ｶﾅ),品名コード１,品名１,品名コード２,品名２,荷扱い１,荷扱い２,記事,ｺﾚｸﾄ代金引換額（税込),内消費税額等,止置き,営業所コード,発行枚数,個数口表示フラグ,請求先顧客コード,請求先分類コード,運賃管理番号,クロネコwebコレクトデータ登録,クロネコwebコレクト加盟店番号,クロネコwebコレクト申込受付番号１,クロネコwebコレクト申込受付番号２,クロネコwebコレクト申込受付番号３,お届け予定ｅメール利用区分,お届け予定ｅメールe-mailアドレス,入力機種,お届け予定ｅメールメッセージ,お届け完了ｅメール利用区分,お届け完了ｅメールe-mailアドレス,お届け完了ｅメールメッセージ,クロネコ収納代行利用区分,クロネコ収納代行利用区分(予備),収納代行請求金額(税込),収納代行内消費税額等,収納代行請求先郵便番号,収納代行請求先住所,収納代行請求先住所（アパートマンション名）,収納代行請求先会社・部門名１,収納代行請求先会社・部門名２,収納代行請求先名(漢字),収納代行請求先名(カナ),収納代行問合せ先名(漢字),収納代行問合せ先郵便番号,収納代行問合せ先住所,収納代行問合せ先住所（アパートマンション名）,収納代行問合せ先電話番号,収納代行管理番号,収納代行品名,収納代行備考,複数口くくりキー,検索キータイトル1,検索キー1,検索キータイトル2,検索キー2,検索キータイトル3,検索キー3,検索キータイトル4,検索キー4,検索キータイトル5,検索キー5,検索キータイトル予備,検索キー予備,投函予定メール利用区分,投函予定メールe-mailアドレス,投函予定メールメッセージ,投函完了メール（お届け先宛）利用区分,投函完了メール（お届け先宛）e-mailアドレス,投函完了メール（お届け先宛）メールメッセージ,投函完了メール（ご依頼主宛）利用区分,投函完了メール（ご依頼主宛）e-mailアドレス,投函完了メール（ご依頼主宛）メールメッセージ\n'
  const rows: DeliveryOrderCsvRow[] = []
  const errors: string[] = []
  orders.forEach((order) => {
    const item = order.items[0]
    if (!item) {
      errors.push(`/store_checkouts/${order.checkout_id}の商品データが存在しません。`)
      return
    }
    const storeShipping = storeShippings.find(
      (storeShipping) => storeShipping.store_checkout_ref.id === order.checkout_id
    )
    if (!storeShipping) {
      errors.push(`/store_checkouts/${order.checkout_id}の配送先情報が存在しません。`)
      return
    }
    const storeDeliveryBread = storeDeliveryBreads.find(
      (storeDeliveryBread) => storeDeliveryBread.bread_ref?.id === item.bread_ref.id
    )
    if (!storeDeliveryBread) {
      errors.push(`/breads/${item.bread_ref.id}の配送設定が存在しません。`)
      return
    }
    const scheduledDeliveryTime = storeShipping.scheduled_delivery_time
    const convertScheduledDeliveryTime = (scheduledDeliveryTime: string): string => {
      switch (scheduledDeliveryTime) {
        case '08:00-12:00':
          return '0812'
        case '14:00-16:00':
          return '1416'
        case '16:00-18:00':
          return '1618'
        case '18:00-20:00':
          return '1820'
        case '19:00-21:00':
          return '1921'
        default:
          return ''
      }
    }
    const {
      name: deliveryName,
      phone_number: deliveryPhoneNumber,
      postal_code: deliveryPostalCode,
      prefecture: deliveryPrefecture,
      city: deliveryCity,
      street: deliveryStreet,
      building: deliveryBuilding,
    } = storeShipping.delivery_address
    rows.push({
      お客様管理番号: '',
      送り状種類: '0',
      クール区分: parseFrozenDeliveryTypeForYamato(storeDeliveryBread.frozen_delivery_type),
      伝票番号: '',
      出荷予定日: scheduledDeliveryDate.replaceAll('-', '/'),
      お届け予定日: parseDateWithHyphen(storeShipping.scheduled_delivery_date).replaceAll('-', '/'),
      配達時間帯: convertScheduledDeliveryTime(scheduledDeliveryTime),
      お届け先コード: '',
      お届け先電話番号: parsePhoneNumber(deliveryPhoneNumber),
      お届け先電話番号枝番: '',
      お届け先郵便番号: deliveryPostalCode,
      お届け先住所: `${deliveryPrefecture}${deliveryCity}${parseHankakuToZenkaku(deliveryStreet)}`,
      お届け先アパートマンション名: parseHankakuToZenkaku(deliveryBuilding).slice(0, 16),
      'お届け先会社・部門１': '',
      'お届け先会社・部門２': '',
      お届け先名: parseHankakuToZenkaku(deliveryName).slice(0, 16),
      'お届け先名(ｶﾅ)': '',
      敬称: '',
      ご依頼主コード: '',
      ご依頼主電話番号: parsePhoneNumber(phoneNumber),
      ご依頼主電話番号枝番: '',
      ご依頼主郵便番号: firstPostalCode + secondPostalCode,
      ご依頼主住所: `${state}${city}${parseHankakuToZenkaku(street)}`,
      ご依頼主アパートマンション: parseHankakuToZenkaku(building).slice(0, 16),
      ご依頼主名: parseHankakuToZenkaku(storeName).slice(0, 16),
      'ご依頼主名(ｶﾅ)': '',
      品名コード１: '',
      品名１: parseHankakuToZenkaku(item.bread_name).slice(0, 25),
      品名コード２: '',
      品名２: '',
      荷扱い１: '',
      荷扱い２: '',
      記事: '',
      'ｺﾚｸﾄ代金引換額（税込)': '',
      内消費税額等: '',
      止置き: '',
      営業所コード: '',
      発行枚数: '',
      個数口表示フラグ: '',
      請求先顧客コード: customerCode,
      請求先分類コード: '',
      運賃管理番号: feeManagementNumber,
      クロネコwebコレクトデータ登録: '',
      クロネコwebコレクト加盟店番号: '',
      クロネコwebコレクト申込受付番号１: '',
      クロネコwebコレクト申込受付番号２: '',
      クロネコwebコレクト申込受付番号３: '',
      お届け予定ｅメール利用区分: '',
      'お届け予定ｅメールe-mailアドレス': '',
      入力機種: '',
      お届け予定ｅメールメッセージ: '',
      お届け完了ｅメール利用区分: '',
      'お届け完了ｅメールe-mailアドレス': '',
      お届け完了ｅメールメッセージ: '',
      クロネコ収納代行利用区分: '',
      'クロネコ収納代行利用区分(予備)': '',
      '収納代行請求金額(税込)': '',
      収納代行内消費税額等: '',
      収納代行請求先郵便番号: '',
      収納代行請求先住所: '',
      '収納代行請求先住所（アパートマンション名）': '',
      '収納代行請求先会社・部門名１': '',
      '収納代行請求先会社・部門名２': '',
      '収納代行請求先名(漢字)': '',
      '収納代行請求先名(カナ)': '',
      '収納代行問合せ先名(漢字)': '',
      収納代行問合せ先郵便番号: '',
      収納代行問合せ先住所: '',
      '収納代行問合せ先住所（アパートマンション名）': '',
      収納代行問合せ先電話番号: '',
      収納代行管理番号: '',
      収納代行品名: '',
      収納代行備考: '',
      複数口くくりキー: '',
      検索キータイトル1: '',
      検索キー1: '',
      検索キータイトル2: '',
      検索キー2: '',
      検索キータイトル3: '',
      検索キー3: '',
      検索キータイトル4: '',
      検索キー4: '',
      検索キータイトル5: '',
      検索キー5: '',
      検索キータイトル予備: '',
      検索キー予備: '',
      投函予定メール利用区分: '',
      '投函予定メールe-mailアドレス': '',
      投函予定メールメッセージ: '',
      '投函完了メール（お届け先宛）利用区分': '',
      '投函完了メール（お届け先宛）e-mailアドレス': '',
      '投函完了メール（お届け先宛）メールメッセージ': '',
      '投函完了メール（ご依頼主宛）利用区分': '',
      '投函完了メール（ご依頼主宛）e-mailアドレス': '',
      '投函完了メール（ご依頼主宛）メールメッセージ': '',
    })
  })
  const _rows: string[] = [header]
  for (let i = 0; i < rows.length; i = (i + 1) | 0) {
    const row = rows[i]
    let strRow = ''
    const keys = Object.keys(row)
    const values = Object.values(row)
    for (let j = 0; j < values.length; j = (j + 1) | 0) {
      let value = values[j]
      // 0から始まる値の場合、CSVでは省略されるので=""で囲む
      if (/^0.+$/.test(keys[j])) value = `="${value}"`
      strRow += value
      // 最終列でなければ区切り文字を追加
      if (j + 1 !== values.length) {
        strRow += ','
      }
    }
    strRow += '\n'
    _rows.push(strRow)
  }
  // CSVの文字化け防止bom
  const bom = new Uint8Array([0xef, 0xbb, 0xbf])
  const blob = new Blob([bom, ..._rows], { type: 'text/csv;charset=utf-8' })
  FileSaver.saveAs(blob, `${filename}.csv`)
  console.error(errors.map((error) => error))
}
